/* ===================================================================
   ChoosePlan -- the CARD-REQUIRED FRONT DOOR + hard-lock plan picker
   (2026-07-06 monetization gate, Brad).

   Full-screen route OUTSIDE the AppShell. Auth-gated (a logged-out visitor is
   bounced to /auth by RequireAuth) but NOT subscription-gated -- this IS the
   screen a tenant with no active subscription is allowed to reach. It is the
   only door (besides the Stripe billing portal) for:
     - a brand-new account that has verified email but never subscribed
       ("Start your 14-day free trial")
     - a lapsed account whose trial ended / subscription was canceled or a
       payment failed ("Your trial has ended -- choose a plan to continue")

   The two modes come off the plan payload (useSubscriptionAccess().mode):
     'new'         -> trial-start copy
     'resubscribe' -> lapsed copy + a "Manage billing" link to the Stripe portal

   Checkout reuses the EXISTING billing API (api.startCheckout, tcpa_consent
   REQUIRED per Hard Rule #6; api.getConsentText for the verbatim ToS/Privacy
   links; api.openBillingPortal for the portal). On success Stripe redirects to
   /settings?billing=success (backend-set); the account is then subscribed, so
   RequireSubscription lets it into the app (App-level CheckoutReturnSync
   refetches the plan so no manual reload is needed).

   PRICING (ONE PRICE, locked 2026-08-06 -- retires the Starter/Pro ladder):
     $59/4 weeks. One plan, every feature included, same for everyone.
     Plain month-to-month, NO annual, NO founding/standard split, NO upfront
     multi-cycle charge: after the 14-day free trial the first charge is one
     4-week cycle ($59), then every 4 weeks.
   Multi-location is +$25/4 weeks per extra location, available to EVERY
   customer. UI copy must NEVER say "no card" or "card required" -- just
   "cancel anytime".

   The feature list below is a SUBSET of the live marketing site's "Everything
   included" list (website_light_astro/src/pages/index.astro) and must not
   outrun it. Note what is deliberately absent: social posting, because Meta
   publishing is not live. Do not add a bullet here that the site does not
   already make -- feedback_never_advertise_what_we_dont_do.

   TOKENS ONLY: every color comes from tokens.css (var(--...)); no hardcoded
   brand hex (hard rule).
   =================================================================== */
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BrandMark } from '../components/BrandMark';
import { LogoutButton } from '../components/LogoutButton';
import { useApiToken } from '../hooks/useApiToken';
import { resolveEntryModel } from '../config/entryModel';
import { useSubscriptionAccess } from '../hooks';
import {
  api,
  type BillingPlan,
  type BillingTier,
  type ConsentText,
} from '../lib/api';
import { track } from '../lib/analytics';
import '../styles/auth.css';
import '../styles/components.css';

/* THE plan. One price, every feature included (Steve, 2026-08-06). Mirrors
   Settings' PLAN_INFO so the front door and the in-app Billing panel show
   identical numbers. */
const PLAN_INFO = {
  name: 'Rhody Reviews',
  price: '$59',
  blurb: 'One plan. Every feature included. Cancel anytime.',
  features: [
    'Automated review requests by text & email',
    'One-tap Google review links + QR codes',
    'AI-drafted replies (you approve every one)',
    'Review inbox for your Google reviews',
    'Google Business Profile posting',
    'Website review widget',
    'Competitor review tracking',
    'Monthly PDF reports',
  ],
} as const;

/* The single plan identifier the API accepts. There is nothing to choose, so
   this is a constant rather than component state. */
const PLAN_TIER: BillingTier = 'basic';

export function ChoosePlan() {
  const hasToken = Boolean(useApiToken());
  const access = useSubscriptionAccess();

  const planQ = useQuery<BillingPlan, Error>({
    queryKey: ['billing-plan'],
    queryFn: ({ signal }) => api.getPlan(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
  const consentQ = useQuery<ConsentText, Error>({
    queryKey: ['billing-consent-text'],
    queryFn: ({ signal }) => api.getConsentText(signal),
    enabled: hasToken,
    staleTime: 5 * 60_000,
  });

  const [locations, setLocations] = useState(0);
  // REQUIRED Terms of Service / Privacy acceptance (blocks checkout).
  const [terms, setTerms] = useState(false);
  // OPTIONAL, separate SMS-sending authorization (declining still subscribes).
  const [smsAuthorized, setSmsAuthorized] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [portalErr, setPortalErr] = useState<string | null>(null);

  const checkout = useMutation({
    mutationFn: () =>
      api.startCheckout({
        tier: PLAN_TIER,
        // Ungated: any customer can buy additional locations (Steve 2026-08-06).
        locations,
        tcpa_consent: terms,
        tcpa_consent_version: consentQ.data?.version,
        sms_authorized: smsAuthorized,
      }),
    onSuccess: (res) => {
      // Hand off to the Stripe-hosted Checkout page (card collected there).
      window.location.href = res.url;
    },
    onError: (e: unknown) => {
      setErr(e instanceof Error ? e.message : 'Could not start checkout. Please try again.');
    },
  });

  const portal = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: (res) => {
      if (res?.url) window.location.href = res.url;
      else setPortalErr('The billing portal is not available yet. To manage billing, email support@rhodyreviews.com.');
    },
    onError: () => {
      setPortalErr('We could not open the billing portal just now. Please try again, or email support@rhodyreviews.com.');
    },
  });

  // A whitelisted operator/demo or an already-subscribed account should never
  // sit on the front door -- send them into the app. (Guards a direct visit to
  // /choose-plan by someone who doesn't need it.)
  if (access.state === 'allowed') {
    return <Navigate to="/dashboard" replace />;
  }

  const resubscribe = access.mode === 'resubscribe';
  const params = new URLSearchParams(window.location.search);
  // SEND-INTENT framing (card-at-send, Steve 2026-07-08): when the owner arrived
  // here from the first review-request send, lead with the send-specific copy so
  // the "why am I adding a card now" is obvious.
  const sendIntent = params.get('intent') === 'send';
  // SMS-INTENT framing (Steve 2026-07-29): arrived from the Requests SMS tab's
  // "see paid plans" nudge -> lead with unlocking texting specifically.
  const smsIntent = params.get('intent') === 'sms';
  // ALREADY-TRIALING CTA FIX (Steve 2026-07-29). Under the LIVE reverse_trial
  // entry model, every /choose-plan viewer is an authenticated, card-free owner
  // ALREADY in their free trial: RequireAuth blocks logged-out visitors, and a
  // subscribed/whitelisted owner is redirected to /dashboard above (state ===
  // 'allowed'). So "Start free trial" is incoherent -- this screen CONVERTS them
  // to paid (adds a card). Only the DORMANT front_door_wall arm has a genuine
  // pre-trial new visitor hard-locked here before reaching the app, which keeps
  // the trial CTA. A lapsed account (resubscribe) keeps its own resubscribe copy.
  const alreadyTrialing =
    resolveEntryModel() === 'reverse_trial' && !resubscribe;
  const info = PLAN_INFO;

  return (
    <div className="auth choose-plan">
      <div className="choose-plan__wrap">
        <header className="choose-plan__head">
          <BrandMark />
          <LogoutButton variant="bar" />
        </header>

        <div className="choose-plan__intro">
          <h1 className="choose-plan__title">
            {resubscribe
              ? 'Your free trial has ended'
              : alreadyTrialing
                ? smsIntent
                  ? 'Add your card to unlock texting'
                  : 'Add your card to go paid'
                : sendIntent
                  ? 'Start your 14-day free trial to send your first request'
                  : 'Start your 14-day free trial'}
          </h1>
          <p className="choose-plan__sub">
            {resubscribe
              ? 'Choose a plan to keep collecting and managing your reviews. Pick up right where you left off.'
              : alreadyTrialing
                ? smsIntent
                  ? "Texting is a paid feature. You're on your free trial now. Add your card to go paid and unlock review-request texts to your own customers. Billed every 4 weeks, cancel anytime."
                  : "You're on your free trial now. Add your card to go paid and keep everything running. Billed every 4 weeks, cancel anytime."
                : sendIntent
                  ? 'Add your card to start your free trial, then send your first review request. No charge today. Billed every 4 weeks, cancel anytime.'
                  : 'Pick the plan that fits, and start collecting reviews today. Billed every 4 weeks, cancel anytime.'}
          </p>
        </div>

        {/* THE plan. Not a chooser -- there is one plan, so this is a summary
            of what the card is for, not a decision to make. */}
        <div className="choose-plan__cards">
          <div className="plan-card plan-card--selected plan-card--only">
            <div className="plan-card__top">
              <b className="plan-card__name">{PLAN_INFO.name}</b>
              <span className="plan-card__tag">Everything included</span>
            </div>
            <div className="plan-card__price">
              <span className="plan-card__amt">{PLAN_INFO.price}</span>
              <span className="plan-card__per">/4 weeks</span>
            </div>
            <span className="plan-card__blurb">{PLAN_INFO.blurb}</span>
            <ul className="plan-card__feats">
              {PLAN_INFO.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Multi-location add-on -- available to EVERY customer (Steve,
            2026-08-06: "the $25 location add-on should be on every single
            one"). It used to render only when the Pro tier was selected. */}
        <label className="fld choose-plan__loc">
          <span className="lab">Additional locations (+$25/4 weeks each)</span>
          <input
            className="inp"
            type="number"
            min={0}
            value={locations}
            onChange={(e) => setLocations(Math.max(0, parseInt(e.target.value || '0', 10)))}
            style={{ maxWidth: 120 }}
          />
          <span className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
            Your plan includes 1 location. Each extra location is +$25/4 weeks, no cap.
          </span>
        </label>

        {/* REQUIRED: Terms of Service / Privacy acceptance (NOT SMS consent).
            Checkout is blocked until this is checked (Hard Rule #6). */}
        <div className="choose-plan__consent">
          <label className="choose-plan__consent-row">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => {
                setErr(null);
                setTerms(e.target.checked);
              }}
            />
            <span>
              I have read and agree to the Rhody Reviews{' '}
              <a className="link" href={consentQ.data?.terms_url ?? 'https://rhodyreviews.com/terms'} target="_blank" rel="noreferrer">Terms of Service</a>{' '}
              and{' '}
              <a className="link" href={consentQ.data?.privacy_url ?? 'https://rhodyreviews.com/privacy'} target="_blank" rel="noreferrer">Privacy Policy</a>.
            </span>
          </label>
        </div>

        {/* OPTIONAL + SEPARATE: SMS-sending authorization. */}
        <div className="choose-plan__consent">
          <label className="choose-plan__consent-row">
            <input
              type="checkbox"
              checked={smsAuthorized}
              onChange={(e) => {
                setErr(null);
                setSmsAuthorized(e.target.checked);
              }}
            />
            <span>
              Optional. I authorize Rhody Reviews to send review-request and follow-up text
              messages to my own customers who have opted in, on behalf of my business. I
              confirm I will only add customers who have given me their consent to be texted.
              I can turn this on or off anytime in Settings. This is not required to subscribe.
              {consentQ.data && (
                <span style={{ display: 'block', marginTop: 6 }}>
                  <a className="link" href={consentQ.data.sms_terms_url} target="_blank" rel="noreferrer">SMS Terms</a>
                </span>
              )}
            </span>
          </label>
        </div>

        <div className="choose-plan__actions">
          <button
            className="btn btn--primary btn--lg"
            onClick={() => {
              setErr(null);
              track('upgrade_clicked', { tier: PLAN_TIER });
              if (!terms) {
                setErr('Please accept the Terms of Service and Privacy Policy to continue.');
                return;
              }
              checkout.mutate();
            }}
            disabled={checkout.isPending || !consentQ.data || planQ.isLoading}
          >
            {checkout.isPending
              ? 'Starting checkout...'
              : resubscribe
                ? `Resubscribe to ${info.name}`
                : alreadyTrialing
                  ? smsIntent
                    ? 'Add your card to unlock texting'
                    : `Go paid now on ${info.name}`
                  : `Start free trial on ${info.name}`}
          </button>
          {err && <span className="choose-plan__err">{err}</span>}
        </div>

        <p className="choose-plan__fine">
          Billed every 4 weeks. Cancel anytime. Your plan stays active through the
          end of your current paid period, then stops. No refund for a period
          already started. No annual plan.
        </p>

        {/* Lapsed accounts get a self-serve billing-portal link too, so they can
            update a failed card or reactivate directly. */}
        {resubscribe && (
          <div className="choose-plan__portal">
            <button
              type="button"
              className="link"
              onClick={() => {
                setPortalErr(null);
                portal.mutate();
              }}
              disabled={portal.isPending}
            >
              {portal.isPending ? 'Opening billing...' : 'Manage billing'}
            </button>
            {portalErr && (
              <p className="muted" style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.5 }}>
                {portalErr}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChoosePlan;
